import type { Ref, UnwrapRef } from "vue";
import useAuth from "./auth";
import type { CancelablePromise } from "@/interfaces/api";
import { ApiError } from "@/interfaces/api";
import { DefaultService, OpenAPI } from "~~/api/njorda";

import { clearUserLoginInfo } from "@/libs/auth";
import type { DataCacheKey } from "@/store/dataCache";
import { useAppStateStore } from "@/store/appState";
import { useUserStore } from "@/store/user";
import { LOGOUT_CLEAR_TIMEOUT } from "@/constants/system";
import { clearEntriesByPrefix, getStorageItem, removeStorageItem, setStorageItem } from "@/libs/local-storage";

const PERSISTENT_CACHE_PREFIX = "njorda-cache-";
const TTL = 1000 * 60 * 60 * 12; // 12 hours

function getUserCachePrefix(): string {
    // Get user ID from the user store to create user-specific cache keys
    const userStore = useUserStore();
    const userId = userStore.id;
    return userId
        ? `${PERSISTENT_CACHE_PREFIX}${userId}-`
        : `${PERSISTENT_CACHE_PREFIX}-`;
}

function getPersistentCache<T>(key: DataCacheKey): T | null {
    try {
        const cachePrefix = getUserCachePrefix();
        return getStorageItem<T>(cachePrefix + key);
    } catch (e) {
        console.error("Error reading persistent cache:", e);
        return null;
    }
}

function setPersistentCache<T>(key: DataCacheKey, data: T): void {
    const cachePrefix = getUserCachePrefix();
    const fullKey = cachePrefix + key;

    setStorageItem(fullKey, data, "Error writing cache:", TTL, false, cachePrefix);
}

type TCacheState = "pending" | "skipped" | "cached" | "live" | "error";
type TState = "pending" | "done" | "error" | "skipped";

interface InMemoryCacheEntry<T> {
    state: TState;
    result: Ref<T | null>;
    pending: Ref<boolean>;
    error: Ref<Error | null>;
    cacheState: Ref<TCacheState>;
    promise: Promise<T | null>;
    t: number;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TLocalCache = Record<DataCacheKey, InMemoryCacheEntry<any>>;
const localCache: TLocalCache = {} as TLocalCache;

type TBindingCache = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cache: InMemoryCacheEntry<any>;
};

function setLoggedIn(loggedIn: boolean) {
    const { setLoggedIn: setAuthLoggedIn, clearLoggedIn } = useAuth();
    // Clear previous cached values
    if (loggedIn) {
        setAuthLoggedIn();
        Object.values(localCache).forEach((cache) => {
            cache.result.value = null;
        });
    } else {
        clearLoggedIn();
        setTimeout(() => {
            Object.values(localCache).forEach((cache) => {
                cache.result.value = null;
            });
        }, LOGOUT_CLEAR_TIMEOUT);
    }
}

function clearCache() {
    // Clear only the current user's cache entries
    clearEntriesByPrefix(getUserCachePrefix());
}

function invalidateCacheKey(key: DataCacheKey): void {
    if (!import.meta.client) return;
    const cachePrefix = getUserCachePrefix();
    removeStorageItem(cachePrefix + key);
    if (localCache[key]) {
        localCache[key].result.value = null;
        localCache[key].cacheState.value = "pending";
    }
}

function invalidateCacheKeysByPrefix(keyPrefix: string): void {
    if (!import.meta.client) return;
    const cachePrefix = getUserCachePrefix();
    clearEntriesByPrefix(cachePrefix + keyPrefix);
    (Object.keys(localCache) as DataCacheKey[]).forEach((key) => {
        const entry = localCache[key];
        if (entry && key.startsWith(keyPrefix)) {
            entry.result.value = null;
            entry.cacheState.value = "pending";
        }
    });
}

export const useNjordaApi = () => {
    const baseUrl = useRuntimeConfig().public.njordaApiUrl;
    const { defaultRoute } = useAuth();
    OpenAPI.BASE = baseUrl;
    OpenAPI.WITH_CREDENTIALS = true;

    return {
        api: DefaultService,
        defaultRoute,
        setLoggedIn,
        clearCache,
        invalidateCacheKey,
        invalidateCacheKeysByPrefix,
    };
};

const initCacheObject = <T>(): InMemoryCacheEntry<T> =>
    ({
        state: "pending",
        result: ref<T | null>(null),
        pending: ref(false),
        error: ref<Error | null>(null),
        cacheState: ref<TCacheState>("pending"),
        promise: Promise.resolve(null),
        t: Date.now(),
    }) as InMemoryCacheEntry<T>;

export function useDataLoaderWithCache<T>(
    apiCallFn: () => CancelablePromise<T>,
    cacheKey: DataCacheKey,
    options?: {
        forceRefetch?: boolean;
        skip?: boolean;
    },
): {
    result: Ref<T | null>;
    pending: Ref<UnwrapRef<boolean>>;
    error: Ref<UnwrapRef<Error | null>>;
    cacheState: Ref<UnwrapRef<TCacheState>>;
    promise: Promise<T | null>;
    refetch: () => Promise<T | null>;
    t: number;
    state: TState;
} {
    const { forceRefetch = false, skip = false } = options ?? {};
    const router = useRouter();
    const { errorToast } = useAppStateStore();

    async function request(this: TBindingCache): Promise<T | null> {
        const cacheObj = this.cache;
        if (cacheObj.pending.value) {
            return cacheObj.result.value;
        }
        cacheObj.pending.value = true;
        cacheObj.state = "pending";
        cacheObj.cacheState.value = "pending";
        const res = await apiCallFn()
            .then((data: T) => {
                // --- Success ---
                cacheObj.pending.value = false;
                cacheObj.error.value = null;
                // Check if data actually changed before updating refs and cache
                // (Simple stringify comparison, adjust if complex objects need deep compare)
                const oldDataString = JSON.stringify(cacheObj.result.value);
                const newDataString = JSON.stringify(data);
                if (oldDataString !== newDataString) {
                    setPersistentCache(cacheKey, data);
                }
                cacheObj.cacheState.value = "live";
                cacheObj.state = "done";

                return data;
            })
            .catch(async (err) => {
                cacheObj.pending.value = false;
                cacheObj.cacheState.value = "error";
                cacheObj.state = "error";

                if (err instanceof ApiError) {
                    if (err.status === 401) {
                        clearUserLoginInfo();
                        const { defaultRoute } = useAuth();
                        // Don't set local error ref on 401, let redirect handle it
                        await router.push(defaultRoute);
                    } else if (err.status === 500) {
                        errorToast(err.message);
                        cacheObj.error.value = err;
                    } else {
                        cacheObj.error.value = err;
                    }
                } else {
                    // Non API related error - probably issue in frontend code
                    cacheObj.error.value = err; // Set local error ref
                    console.error("Unhandled error in useDataLoader:", err);
                }
                return null; // Resolve promise with null on error as before
            });

        cacheObj.result.value = res;
        return res;
    }

    if (localCache[cacheKey]?.state === "pending") {
        return {
            ...localCache[cacheKey],
            refetch: request.bind({
                cache: localCache[cacheKey],
            }),
        };
    }

    if (!localCache[cacheKey]) {
        localCache[cacheKey] = initCacheObject<T>();
    }

    // If we reached here, no identical request is currently pending in memory.
    // Store the refs for this new request in the in-memory cache.
    // --- Persistent Cache Check (Initial Load) ---
    if (!forceRefetch && localCache[cacheKey]?.cacheState.value !== "live") {
        const persistentData = getPersistentCache<T>(cacheKey);

        if (persistentData) {
            localCache[cacheKey].result.value = persistentData;
            localCache[cacheKey].cacheState.value = "cached";
            localCache[cacheKey].pending.value = false;
        }
    }

    if (forceRefetch || localCache[cacheKey].cacheState.value !== "live") {
        if (!skip) {
            localCache[cacheKey].state = "pending";
            localCache[cacheKey].promise = request.call({
                cache: localCache[cacheKey],
            });
        } else {
            localCache[cacheKey].state = "skipped";
            localCache[cacheKey].cacheState.value = "skipped";
            localCache[cacheKey].result.value = null;
            localCache[cacheKey].promise = Promise.resolve(null);
        }
    }

    return {
        ...localCache[cacheKey],
        refetch: request.bind({
            cache: localCache[cacheKey],
        }),
    };
}

/**
 * Load data from an api call, and cache the result.
 * @param apiCallFn
 * @param cacheKey
 * @param options
 */
export function useDataLoader<T>(apiCallFn: () => CancelablePromise<T>): {
    result: Ref<T | null>;
    pending: Ref<UnwrapRef<boolean>>;
    error: Ref<UnwrapRef<Error | null>>;
    cacheState: Ref<UnwrapRef<TCacheState>>;
    promise: Promise<T | null>;
    refetch: () => Promise<T | null>;
} {
    const router = useRouter();
    const { errorToast } = useAppStateStore();

    // These will be potentially overwritten by in-memory cache if a request is in flight
    const result = ref<T | null>(null) as Ref<T | null>;
    const pending = ref(false);
    const error = ref<Error | null>(null);
    const cacheState = ref<TCacheState>("skipped");

    // --- Prepare for API Call ---
    // If we reached here, no identical request is currently pending in memory.
    // Store the refs for this new request in the in-memory cache.

    function request(): Promise<T | null> {
        pending.value = true;
        return apiCallFn()
            .then((data: T) => {
                result.value = data;
                pending.value = false;
                error.value = null;
                cacheState.value = "live";
                return data;
            })
            .catch(async (err) => {
                pending.value = false;
                cacheState.value = "error";
                if (err instanceof ApiError) {
                    if (err.status === 401) {
                        clearUserLoginInfo();
                        const { defaultRoute } = useAuth();
                        await router.push(defaultRoute);
                    } else if (err.status === 500) {
                        errorToast(err.message);
                        error.value = err;
                    } else {
                        error.value = err;
                    }
                } else {
                    error.value = err;
                    console.error("Unhandled error in useDataLoader (no cache):", err);
                }
                return null;
            });
    }

    const promise: Promise<T | null> = request();

    return { result, pending, error, cacheState, promise, refetch: request };
}

/**
 * Wrap an api call in a try/catch block that will redirect to login
 * if the call returns a 401.
 * @param apiCall
 */
export async function wrapApi<T>(apiCall: Promise<T>): Promise<T> {
    const router = useRouter();

    try {
        return await apiCall;
    } catch (error) {
        if (error instanceof ApiError) {
            if (error.status === 401) {
                clearUserLoginInfo();
                const { defaultRoute } = useAuth();
                await router.push(defaultRoute);
            } else if (error.status === 500) {
                const { errorToast } = useAppStateStore();
                errorToast(error.message);
            }
            // We neeed this throw to not return an undefined, this causes
            // some noise in the console, but is better than having to check all
            // returned values for  undefined.
            throw error;
        } else {
            throw error;
        }
    }
}
